import React from 'react'
import { useSelector } from 'react-redux'
import Slider from 'react-slick'
import { Card, Box, Stack, Typography, Chip, Avatar } from '@mui/material'
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Financial Investment Icons
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import EnergyIcon from '@mui/icons-material/ElectricBolt';
import ComputerIcon from '@mui/icons-material/Computer';
import HomeIcon from '@mui/icons-material/Home';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SchoolIcon from '@mui/icons-material/School';
import AgricultureIcon from '@mui/icons-material/Grass';

const InvestmentSlide = () => {

  const appMode = useSelector((state) => state.mode.mode);
 const dispatch = useDispatch();
  const navigate = useNavigate();

  // Investment opportunities data
  const investmentOpportunities = [
    {
      id: 1,
      name: "Green Energy Fund",
      code: "GEF",
      expectedReturn: 14.5,
      riskLevel: "Medium",
      category: "Sustainable Energy",
      minInvestment: 1000,
      duration: "24 months",
      icon: <EnergyIcon />,
      color: "#4caf50"
    },
    {
      id: 2,
      name: "Tech Innovation Fund",
      code: "TIF",
      expectedReturn: 22.3,
      riskLevel: "High",
      category: "Technology",
      minInvestment: 2500,
      duration: "36 months",
      icon: <ComputerIcon />,
      color: "#2196f3"
    },
    {
      id: 3,
      name: "Real Estate Development",
      code: "RED",
      expectedReturn: 11.8,
      riskLevel: "Low",
      category: "Real Estate",
      minInvestment: 5000,
      duration: "18 months",
      icon: <HomeIcon />,
      color: "#ff9800"
    },
    {
      id: 4,
      name: "Healthcare Innovation",
      code: "HIF",
      expectedReturn: 18.7,
      riskLevel: "Medium",
      category: "Healthcare",
      minInvestment: 1500,
      duration: "30 months",
      icon: <LocalHospitalIcon />,
      color: "#f44336"
    },
    {
      id: 5,
      name: "Education Technology",
      code: "EDT",
      expectedReturn: 16.2,
      riskLevel: "Medium",
      category: "EdTech",
      minInvestment: 1200,
      duration: "24 months",
      icon: <SchoolIcon />,
      color: "#9c27b0"
    },
    {
      id: 6,
      name: "Agriculture Innovation",
      code: "AGI",
      expectedReturn: 13.4,
      riskLevel: "Low",
      category: "Agriculture",
      minInvestment: 800,
      duration: "20 months",
      icon: <AgricultureIcon />,
      color: "#8bc34a"
    }
  ];

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
        }
      }
    ]
  }

  const getRiskColor = (risk) => {
    switch(risk) {
      case 'Low': return '#4caf50';
      case 'Medium': return '#ff9800';
      case 'High': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Card sx={{ 
      mb: 3,
      background: appMode === 'dark' ? '#1a1a1a' : '#f8f9fa',
      border: appMode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0'
    }}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ 
          mb: 2, 
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <BusinessCenterIcon color="primary" />
          Featured Investment Opportunities
        </Typography>
        
      <Slider {...settings}>
          {investmentOpportunities.map((investment) => (
            <Box key={investment.id} sx={{ px: 1 }}>
              <Card
                onClick={() => {
                  // Navigate to investment details page
                  navigate(`/investment-details/${investment.id}`);
                }}
                sx={{
                  height: 180,
                  cursor: 'pointer',
                  background: appMode === 'dark' 
                    ? 'linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)'
                    : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                  border: `2px solid ${investment.color}20`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 25px ${investment.color}40`,
                    border: `2px solid ${investment.color}60`,
                    transition: 'all 0.3s ease'
                  }
                }}
              >
                <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Avatar sx={{ 
                      bgcolor: `${investment.color}20`, 
                      color: investment.color,
                      width: 32,
                      height: 32
                    }}>
                      {investment.icon}
                    </Avatar>
                    <Chip 
                      label={investment.riskLevel}
                      size="small"
                      sx={{
                        bgcolor: getRiskColor(investment.riskLevel),
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: '0.7rem'
                      }}
                    />
                  </Box>

                  {/* Investment Details */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ 
                      fontWeight: 'bold', 
                      mb: 0.5,
                      fontSize: '0.95rem'
                    }}>
                      {investment.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ 
                      display: 'block',
                      mb: 1
                    }}>
                      {investment.category} • {investment.duration}
                    </Typography>

                    {/* Expected Return */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                      <Typography variant="body2" sx={{ 
                        color: '#4caf50',
                        fontWeight: 'bold'
                      }}>
                        {investment.expectedReturn}% Expected Return
                      </Typography>
                    </Box>

                    {/* Minimum Investment */}
                    <Typography variant="caption" color="text.secondary">
                      Min. Investment: {formatCurrency(investment.minInvestment)}
                    </Typography>
                  </Box>

                  {/* Code */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center',
                    mt: 'auto'
                  }}>
                    <Chip 
                      label={investment.code}
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: investment.color,
                        color: investment.color,
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                </Box>
              </Card>
            </Box>
        ))}
      </Slider>
      </Box>

      {/* Custom Styles */}
      <style jsx>{`
        .slick-prev:before,
        .slick-next:before {
          color: #1976d2 !important;
          font-size: 20px !important;
        }
        .slick-prev,
        .slick-next {
          z-index: 1;
        }
        .slick-prev {
          left: 10px;
        }
        .slick-next {
          right: 10px;
        }
      `}</style>
    </Card>
  )
}

export default InvestmentSlide
